import { useState, useRef, useEffect } from 'react';
import { PDFDocumentProxy, getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import { PDFDocument, degrees } from 'pdf-lib';
import { Button } from "./ui/button";
import { Download, RotateCw } from "lucide-react";
import { toast } from "sonner";

// Configure PDF.js worker
const pdfjsVersion = '3.11.174';
GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`;

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  image: any; // PDF-lib image object
  pageIndex: number;
  imageIndex: number; // Track which original image this is
}

interface InteractivePreviewProps {
  pdfFile: File;
  imageFiles: File[];
  scale: number;
  rotation: number;
  onSave: (pdfDoc: Uint8Array) => Promise<void>;
  onCancel: () => void;
}

export const InteractivePreview = ({
  pdfFile,
  imageFiles,
  scale,
  rotation,
  onSave,
  onCancel,
}: InteractivePreviewProps) => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [pdfJsDoc, setPdfJsDoc] = useState<PDFDocumentProxy | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState<number | null>(null);
  const [isRotating, setIsRotating] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [availableImages, setAvailableImages] = useState<{image: any, index: number}[]>([]);

  // Load PDF and initialize
  useEffect(() => {
    const loadPdf = async () => {
      const pdfBytes = await pdfFile.arrayBuffer();
      // Clone buffer to avoid detachment between pdfjs and pdf-lib
      const pdfBytesCopyForJs = pdfBytes.slice(0);
      const pdfBytesCopyForLib = pdfBytes.slice(0);

      const _pdfJsDoc = await getDocument({ data: new Uint8Array(pdfBytesCopyForJs) }).promise;
      setNumPages(_pdfJsDoc.numPages);
      setPdfJsDoc(_pdfJsDoc);
      
      // Load with PDF-lib for manipulation
      const _pdfDoc = await PDFDocument.load(pdfBytesCopyForLib);
      setPdfDoc(_pdfDoc);
    };
    loadPdf();
  }, [pdfFile]);

  // Convert image files to URLs for display
  useEffect(() => {
    const urls = imageFiles.map(file => URL.createObjectURL(file));
    setImageUrls(urls);
    
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imageFiles]);

  // Initialize available images and positions
  useEffect(() => {
    if (!pdfDoc || imageFiles.length === 0) return;

    const initialize = async () => {
      // Embed all images first
      const embeddedImages = await Promise.all(
        imageFiles.map(async (file, index) => {
          const bytes = await file.arrayBuffer();
          const bytesCopy = bytes.slice(0);
          const image = await pdfDoc.embedPng(bytesCopy);
          return { image, index };
        })
      );
      
      setAvailableImages(embeddedImages);
      
      // Initialize with one image per page if no positions exist
      if (positions.length === 0) {
        const pages = pdfDoc.getPages();
        const newPositions: Position[] = [];
        
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
          const page = pages[pageIndex];
          const { width, height } = page.getSize();
          
          // Select an image for this page (cycle through available images)
          const imageIndex = pageIndex % embeddedImages.length;
          const { image } = embeddedImages[imageIndex];
          
          // Fixed size for the image (1600x800px)
          const imgWidth = 1200 * scale;
          const imgHeight = 600 * scale;
          
          // Calculate position in lower 75% of the page
          const minY = height * 0.65;  // Start from 55% from top (lower 75% of page)
          const maxY = height - imgHeight - 10;  // Bottom of page - image height - padding
          const minX = 10;  // Small padding from left
          const maxX = width - imgWidth - 10;  // Right edge - image width - padding
          
          // Center horizontally, random vertical position in lower 75%
          const x = minX + Math.random() * (maxX - minX);  // Center horizontally
          const y = minY + Math.random() * (maxY - minY);  // Random in lower 75%
          
          // Random rotation between -5 and 5 degrees
          const randomRotation = (Math.random() * 10 - 5);
          
          newPositions.push({
            x,
            y,
            width: imgWidth,
            height: imgHeight,
            rotation: randomRotation,
            image,
            pageIndex,
            imageIndex,
          });
        }
        
        setPositions(newPositions);
      }
      
      renderPage();
    };

    initialize();
  }, [pdfDoc, imageFiles, scale, rotation]);

  // Render the current page with images
  const renderPage = async () => {
    if (!canvasRef.current || !pdfJsDoc) return;
    
    const canvas = canvasRef.current;
    const page = await pdfJsDoc.getPage(currentPage + 1);
    const viewport = page.getViewport({ scale: 1 });
    
    // Set canvas dimensions and sync container size for overlays
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    if (containerRef.current) {
      containerRef.current.style.width = `${viewport.width}px`;
      containerRef.current.style.height = `${viewport.height}px`;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Render PDF page using PDF.js, cancelling any in-progress render
    // Keep a render task on the canvas element to avoid duplicate renders
    const anyCanvas = canvas as any;
    
    // Cancel any existing render task
    if (anyCanvas.__renderTask) {
      try { 
        anyCanvas.__renderTask.cancel();
        anyCanvas.__renderTask = null;
      } catch (e) {
        // Ignore cancellation errors
        if (!(e instanceof Error && e.message.includes('Rendering cancelled'))) {
          console.warn('Error cancelling previous render task:', e);
        }
      }
    }
    
    // Create a new render task
    const renderTask = page.render({ canvasContext: ctx, viewport });
    anyCanvas.__renderTask = renderTask;
    
    try {
      await renderTask.promise;
    } catch (error) {
      // Ignore cancellation errors
      if (!(error instanceof Error && error.message.includes('Rendering cancelled'))) {
        console.error('Error rendering PDF page:', error);
      }
    } finally {
      // Only clear the task if it's still the current one
      if (anyCanvas.__renderTask === renderTask) {
        anyCanvas.__renderTask = null;
      }
    }
  };

  // Ensure first render when pdfJsDoc becomes available
  useEffect(() => {
    renderPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfJsDoc]);

  // Handle mouse down on an image
  const handleMouseDown = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const pos = positions[index];
    dragOffset.current = {
      x: x - pos.x,
      y: y - pos.y
    };
    
    setIsDragging(index);
  };

  // Handle mouse move for dragging, resizing, and rotating
  const handleMouseMove = (e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Handle dragging
    if (isDragging !== null) {
      const x = mouseX - dragOffset.current.x;
      const y = mouseY - dragOffset.current.y;
      
      setPositions(prev => {
        const newPositions = [...prev];
        newPositions[isDragging] = {
          ...newPositions[isDragging],
          x,
          y
        };
        return newPositions;
      });
    }
    
    // Handle resizing
    if (isResizing !== null) {
      const pos = positions[isResizing];
      const centerX = pos.x + pos.width / 2;
      const centerY = pos.y + pos.height / 2;
      
      const distance = Math.sqrt(
        Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2)
      );
      
      const newWidth = distance * 2;
      const aspectRatio = pos.height / pos.width;
      const newHeight = newWidth * aspectRatio;
      
      setPositions(prev => {
        const newPositions = [...prev];
        newPositions[isResizing] = {
          ...newPositions[isResizing],
          width: newWidth,
          height: newHeight,
          x: centerX - newWidth / 2,
          y: centerY - newHeight / 2
        };
        return newPositions;
      });
    }
    
    // Handle rotating
    if (isRotating !== null) {
      const pos = positions[isRotating];
      const centerX = pos.x + pos.width / 2;
      const centerY = pos.y + pos.height / 2;
      
      const angle = Math.atan2(mouseY - centerY, mouseX - centerX);
      const deg = (angle * 180) / Math.PI + 90;
      
      setPositions(prev => {
        const newPositions = [...prev];
        newPositions[isRotating] = {
          ...newPositions[isRotating],
          rotation: deg
        };
        return newPositions;
      });
    }
  };

  // Handle mouse up to end dragging, resizing, and rotating
  const handleMouseUp = () => {
    setIsDragging(null);
    setIsResizing(null);
    setIsRotating(null);
  };

  // Handle page navigation
  const goToPage = (page: number) => {
    if (page >= 0 && page < numPages) {
      setCurrentPage(page);
    }
  };

  // Save the final PDF with all modifications
  const handleSave = async () => {
    if (!pdfDoc) return;
    
    setIsProcessing(true);
    try {
      // Apply all positions to the PDF
      const pages = pdfDoc.getPages();
      
      // Clear existing content (optional, depends on your needs)
      for (const page of pages) {
        // Clear existing drawings if needed
      }
      
      // Draw all images at their final positions (convert from canvas px to PDF points)
      for (const pos of positions) {
        if (pos.pageIndex >= pages.length) continue;
        const page = pages[pos.pageIndex];
        
        // Get the original image from the available images
        const imgData = availableImages.find(img => img.index === pos.imageIndex);
        if (!imgData) continue;
        
        // Get page dimensions for coordinate conversion
        const { width: pageWidth, height: pageHeight } = page.getSize();
        
        // Get viewport dimensions from pdf.js at scale 1
        const pdfJsPage = await pdfJsDoc!.getPage(pos.pageIndex + 1);
        const viewport = pdfJsPage.getViewport({ scale: 1 });
        
        // Calculate ratios for coordinate conversion
        const ratioX = viewport.width / pageWidth;
        const ratioY = viewport.height / pageHeight;
        
        // Convert canvas coordinates to PDF points
        const pdfX = pos.x / ratioX;
        const pdfY = (pageHeight) - ((pos.y + pos.height) / ratioY);
        const pdfWidth = pos.width / ratioX;
        const pdfHeight = pos.height / ratioY;
        
        // Draw the image at the specified position
        page.drawImage(imgData.image, {
          x: pdfX,
          y: pdfY,
          width: pdfWidth,
          height: pdfHeight,
          rotate: degrees(pos.rotation),
        });
      }
      
      // Save the modified PDF
      const modifiedPdfBytes = await pdfDoc.save();
      await onSave(modifiedPdfBytes);
      
    } catch (error) {
      console.error('Error saving PDF:', error);
      toast.error('Failed to save PDF');
    } finally {
      setIsProcessing(false);
    }
  };

  // Render only when page or document changes
  useEffect(() => {
    renderPage();
  }, [currentPage, pdfJsDoc]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
        <div 
          ref={containerRef}
          className="relative bg-white shadow-lg"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <canvas 
            ref={canvasRef} 
            className="border border-gray-200"
            style={{ zIndex: 0 }}
          />
          
          {/* Render draggable images with controls */}
          {positions
            .filter(pos => pos.pageIndex === currentPage)
            .map((pos, idx) => {
              const globalIndex = positions.findIndex(p => p === pos);
              const isActive = isDragging === globalIndex || isResizing === globalIndex || isRotating === globalIndex;
              const imgSrc = imageUrls[pos.imageIndex];
              if (!imgSrc) return null;
              
              return (
                <div
                  key={globalIndex}
                  className="absolute select-none group"
                  style={{
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    width: `${pos.width}px`,
                    height: `${pos.height}px`,
                    transform: `rotate(${pos.rotation}deg)`,
                    transformOrigin: 'center center',
                  }}
                >
                  <img 
                    src={imageUrls[pos.imageIndex]} 
                    alt={`Image ${pos.imageIndex + 1}`}
                    className="w-full h-full object-contain select-none"
                    style={{
                      border: isActive ? '2px solid #3b82f6' : '1px dashed rgba(59, 130, 246, 0.5)',
                      cursor: 'move',
                      zIndex: 1,
                    }}
                    draggable={false}
                    onMouseDown={(e) => handleMouseDown(e, globalIndex)}
                  />
                  
                  {/* Resize handle (bottom-right corner) */}
                  <div
                    className="absolute"
                    style={{
                      bottom: '-6px',
                      right: '-6px',
                      width: '14px',
                      height: '14px',
                      backgroundColor: '#3b82f6',
                      border: '2px solid white',
                      borderRadius: '50%',
                      cursor: 'nwse-resize',
                      opacity: isActive ? 1 : 0,
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsResizing(globalIndex);
                    }}
                  />
                  
                  {/* Rotation handle (top center) */}
                  <div
                    className="absolute"
                    style={{
                      top: '-20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '14px',
                      height: '14px',
                      backgroundColor: '#10b981',
                      border: '2px solid white',
                      borderRadius: '50%',
                      cursor: 'grab',
                      opacity: isActive ? 1 : 0,
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsRotating(globalIndex);
                    }}
                  />
                </div>
              );
            })}
        </div>
      </div>
      
      <div className="border-t border-gray-200 p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {currentPage + 1} of {numPages}
          </span>
          <Button 
            variant="outline" 
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages - 1}
          >
            Next
          </Button>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <RotateCw className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Save PDF
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
